import { useMobile } from 'common-nextjs';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import Dropzone from 'react-dropzone';
import ErrorMessage from '~/components/Form/ErrorMessage';
import ListFlowPhotoDescription from '~/components/ListFlow/Descriptions/ListFlowPhotoDescription';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import DraggablePhoto from '~/components/ListFlow/PhotoSection/DraggablePhoto';
import SuggestedAngles from '~/components/ListFlow/PhotoSection/SuggestedAngles';
import {
  LF_MAX_IMAGES,
  addNewPhoto,
  getListFlowImages,
  swapPhotoPositions,
} from '~/store/listFlow/slices/photos';
import { getListFlowImageErrors } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowPhotoSection: React.FC<Props> = () => {
  const mobile = useMobile();
  const errors = useAppSelector(getListFlowImageErrors);
  const images = useAppSelector(getListFlowImages);
  const dispatch = useAppDispatch();

  async function handleDropAccepted(acceptedFiles: File[]) {
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const objectUrl = URL.createObjectURL(file);

      dispatch(addNewPhoto({ objectUrl, file }));
    }
  }

  return (
    <ListFlowSection title="Photos" description={<ListFlowPhotoDescription />}>
      <div className="grid grid-cols-3 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {/* Ignore this crappy library's bad typing */}
        {/* @ts-ignore */}
        <DndProvider
          backend={mobile ? TouchBackend : HTML5Backend}
          options={{ enableHoverOutsideTarget: true, ignoreContextMenu: true }}
        >
          {images.map((img, i) => (
            <DraggablePhoto
              key={img.url}
              onMove={(a, b) => {
                dispatch(swapPhotoPositions({ oldIndex: a, newIndex: b }));
              }}
              photo={img}
              index={i}
            />
          ))}

          {images.length < LF_MAX_IMAGES && (
            <Dropzone
              multiple
              accept="image/*"
              onDropAccepted={handleDropAccepted}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className="aspect-w-1 aspect-h-1">
                  <div className="border-slate-green-500 text-slate-green-500 flex cursor-pointer items-center justify-center rounded border-2 border-dashed px-2 text-center">
                    <input {...getInputProps()} />
                    Tap or drop your photo
                  </div>
                </div>
              )}
            </Dropzone>
          )}
        </DndProvider>
      </div>

      <div className="mt-4 text-sm italic">
        Click upload or drag and drop photos
      </div>

      <ErrorMessage errors={errors} />

      {!mobile && (
        <div className="mt-4">
          <div className="mb-2 font-bold italic">Suggested angles</div>
          <SuggestedAngles />
        </div>
      )}
    </ListFlowSection>
  );
};

export default ListFlowPhotoSection;
